import { Button } from '@/atoms/Button'
import { FlexColumn } from '@/atoms/FlexContainers'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { TicketIcon } from '@/atoms/Icons/TicketIcon'
import { CaptionSM, HeadingXS, LabelSM, ParagraphMD, TitleXS } from '@/atoms/Text'
import { useTranslate } from '@/utils/translate/translate-client'
import { DefaultTicketsStep } from './DefaultTicketsStep'

interface DefaultStepProps {
  handleGiftClick: () => void
  onClose: () => void
  onCopyClick: () => void
  showtimesLink: string
  source: 'tickets' | 'landing' | 'default'
  discountCodes: string[]
}
export const DefaultStep = ({
  handleGiftClick,
  onClose,
  onCopyClick,
  showtimesLink,
  source = 'default',
  discountCodes,
}: DefaultStepProps) => {
  const { t } = useTranslate('common')

  if (source === 'tickets') {
    return (
      <DefaultTicketsStep
        handleGiftClick={handleGiftClick}
        onClose={onClose}
        onCopyClick={onCopyClick}
        showtimesLink={showtimesLink}
        discountCodes={discountCodes}
      />
    )
  }

  return (
    <>
      <div className="absolute right-6 top-6 cursor-pointer rounded-full bg-black/10 p-2" onClick={onClose}>
        <CloseIcon color="black" size={16} />
      </div>
      <FlexColumn className="gap-8 pb-8">
        <FlexColumn className="gap-2">
          <div className="mx-auto mb-4 hidden rounded-full bg-warning-50 p-[1.125rem] md:block">
            <TicketIcon size={32} color="warm-03" />
          </div>
          <div className="mx-auto mb-4 rounded-full bg-warning-50 p-4 md:hidden">
            <TicketIcon size={24} color="warm-03" />
          </div>
          <HeadingXS weight="bold" className="text-[1.25rem] leading-[2rem] -tracking-[0.03125rem] md:text-[1.75rem]">
            {t('shareTheLightHeading', 'Share the Light')}
          </HeadingXS>
          <ParagraphMD
            weight="medium"
            color="core-gray-700"
            className="text-[0.875rem] leading-[1.625rem] md:text-base md:leading-[1.75rem]"
          >
            {t(
              'shareTheLightGuild',
              'Use your tickets to invite someone or gift them to someone who needs a little light.',
            )}
          </ParagraphMD>
        </FlexColumn>
      </FlexColumn>
      <FlexColumn className="gap-2">
        <FlexColumn className="gap-3 rounded-xl border border-core-gray-200 bg-core-gray-100 md:gap-8">
          <div className="flex flex-col gap-3 p-3 md:flex-row md:gap-6 md:p-4">
            <FlexColumn className="items-start gap-1.5 md:gap-2">
              <TitleXS className="md:photon-title-sm" weight="semibold">
                {t('shareYourTickets', 'Share Your Tickets')}
              </TitleXS>
              <CaptionSM color="core-gray-700" weight="medium" className="md:photon-paragraph-sm md:text-left">
                {t('ShareYourGuildTicketsDescription', 'Send your tickets to a friend or family member.')}
              </CaptionSM>
            </FlexColumn>
            <FlexColumn className="md:justify-center">
              <Button
                onClick={onCopyClick}
                className="min-w-[9.375rem] items-center gap-1.5 rounded-[0.875rem] px-5 py-3 text-sm font-normal md:!py-3"
                variant="black"
              >
                <LabelSM className="md:hidden" weight="semibold">
                  {t('shareMyTickets', 'Share My Tickets')}
                </LabelSM>
                <span className="hidden md:block">{t('copyInviteLink', 'Copy Invite Link')}</span>
              </Button>
            </FlexColumn>
          </div>
        </FlexColumn>

        <FlexColumn className="gap-3 rounded-xl border border-core-gray-200 bg-core-gray-100 md:gap-8">
          <div className="flex flex-col gap-4 p-3 md:flex-row md:gap-6 md:p-4">
            <FlexColumn className="items-start gap-1.5 text-left md:gap-2">
              <TitleXS className="md:photon-title-sm" weight="semibold">
                {t('letAngelGiftThem', 'Let Angel Gift Them')}
              </TitleXS>
              <CaptionSM color="core-gray-700" weight="medium" className="md:photon-paragraph-sm md:text-left">
                {t('giftYourGuildTicketsDescription', "Can't use your tickets? We'll gift your tickets for you.")}
              </CaptionSM>
            </FlexColumn>
            <FlexColumn className="md:justify-center">
              <Button
                onClick={handleGiftClick}
                className="min-w-[9.375rem] items-center gap-1.5 rounded-[0.875rem] px-5 py-3 text-sm font-normal"
                variant="oxide"
              >
                {t('giftMyTickets', 'Gift My Tickets')}
              </Button>
            </FlexColumn>
          </div>
        </FlexColumn>
      </FlexColumn>
    </>
  )
}
